import { getAssistantSurface } from './assistant-actions'
import type { ExecuteClientToolDeps } from './client-tool-types'

/** The pages `show_monitoring` opens, as the model names them. */
const VIEWS = ['sites', 'site', 'runs', 'run'] as const
type MonitoringView = (typeof VIEWS)[number]

const WINDOWS = ['1h', '6h', '24h', '7d', '30d'] as const

/** Matches the server's cap on an id in the page context. */
const MAX_ID = 64

/** A page reads its data before it reports it; past this the page is shown without a claim. */
const SHOW_WAIT_MS = 4_000
const POLL_MS = 100

const delay = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms))

interface ShowRequest {
  view: MonitoringView
  siteId?: string
  runId?: string
  window?: string
}

function readId(value: unknown): string | null | undefined {
  if (value === undefined) return undefined
  if (typeof value !== 'string' || value.trim() === '' || value.trim().length > MAX_ID) return null

  return value.trim()
}

function readRequest(input: unknown): ShowRequest | { error: string } {
  const raw = (input ?? {}) as { view?: unknown; site_id?: unknown; run_id?: unknown; window?: unknown }
  const rawView = raw.view === undefined ? 'sites' : raw.view

  if (!VIEWS.includes(rawView as MonitoringView)) {
    return { error: "Invalid view: expected 'sites', 'site', 'runs' or 'run'." }
  }

  const view = rawView as MonitoringView
  const siteId = readId(raw.site_id)
  const runId = readId(raw.run_id)

  if (siteId === null) return { error: 'Invalid site_id.' }
  if (runId === null) return { error: 'Invalid run_id.' }

  if (raw.window !== undefined && !WINDOWS.includes(raw.window as (typeof WINDOWS)[number])) {
    return { error: "Invalid window: expected '1h', '6h', '24h', '7d' or '30d'." }
  }

  if (view === 'site') {
    if (!siteId) return { error: "The 'site' view needs a site_id." }

    return { view, siteId, ...(raw.window ? { window: raw.window as string } : {}) }
  }

  if (view === 'run') {
    if (!runId) return { error: "The 'run' view needs a run_id." }

    return { view, runId }
  }

  return { view }
}

/** The route the page lives on, with no query: what the route manifest is asked about. */
function routeOf(req: ShowRequest): string {
  if (req.view === 'site') return `/monitoring/${encodeURIComponent(req.siteId!)}`
  if (req.view === 'run') return `/capacity/${encodeURIComponent(req.runId!)}`
  if (req.view === 'runs') return '/capacity'

  return '/monitoring'
}

const queryOf = (req: ShowRequest): string => (req.view === 'site' && req.window ? `preset=${req.window}` : '')

/** True once the page on screen reports the requested view (and the requested id, once loaded). */
function showing(req: ShowRequest): boolean {
  const handle = getAssistantSurface()

  if (req.view === 'sites' || req.view === 'site') {
    if (handle?.kind !== 'monitoring') return false

    const ctx = handle.readContext().monitoring
    // A page that is still (re)reading reports the previous page's numbers.
    if (ctx.loading) return false
    if (req.view === 'sites') return ctx.view === 'library'
    if (ctx.view !== 'site' || ctx.site?.id !== req.siteId) return false

    return req.window ? ctx.window?.preset === req.window : true
  }

  if (handle?.kind !== 'capacity') return false

  const ctx = handle.readContext().capacity
  if (ctx.loading) return false
  if (req.view === 'runs') return ctx.view === 'runs'

  return ctx.view === 'run' && ctx.run?.id === req.runId
}

async function awaitShowing(req: ShowRequest): Promise<boolean> {
  const deadline = Date.now() + SHOW_WAIT_MS

  for (;;) {
    if (showing(req)) return true
    if (Date.now() >= deadline) return false
    await delay(POLL_MS)
  }
}

/**
 * `show_monitoring`. The server gate has already checked the page this call opens, and
 * `refusalFor` checks it again in the browser: `/capacity` for the run views. It returns the route manifest's answer for this caller, or null when allowed.
 */
export async function executeShowMonitoring(
  input: unknown,
  deps: ExecuteClientToolDeps,
  refusalFor: (route: string) => string | null,
): Promise<unknown> {
  const req = readRequest(input)
  if ('error' in req) return req

  const path = routeOf(req)
  const refusal = refusalFor(path)
  if (refusal) return { refused: refusal }

  const query = queryOf(req)
  const target = query ? `${path}?${query}` : path
  const onTargetPage = deps.currentRoute() === path
  const discard = (input as { discardUnsaved?: unknown } | null)?.discardUnsaved === true

  if (!onTargetPage && !discard) {
    const unsaved = deps.hasUnsavedWork()
    if (unsaved) return { blocked_unsaved_work: unsaved }
  }

  // Showing what is already shown must not add a Back entry.
  const currentQuery = (deps.currentSearch?.() ?? '').replace(/^\?/, '')
  if (onTargetPage && currentQuery === query) deps.navigate(target, { replace: true })
  else deps.navigate(target)

  const id = req.siteId ? { site_id: req.siteId } : req.runId ? { run_id: req.runId } : {}

  return (await awaitShowing(req))
    ? { shown: true, view: req.view, ...id }
    : {
        shown: true,
        note: `The ${req.view === 'runs' || req.view === 'run' ? 'Capacity' : 'Monitoring'} page did not report this view in time; it is open but may still be loading.`,
      }
}
